/**
 * Counterparty Service
 *
 * Handles all counterparty-related API calls.
 */

import api from './api';
import type { ApiResponse, Counterparty, CounterpartyAutocompleteResult, CreateCounterpartyRequest, UpdateCounterpartyRequest, MergeCounterpartyResponse } from '@budgira/shared';

export const counterpartyService = {
  /**
   * Get all counterparties for the authenticated user
   */
  async list(): Promise<Counterparty[]> {
    const response = await api.get<ApiResponse<Counterparty[]>>('/counterparties');
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to fetch counterparties');
    }
    return response.data.data;
  },

  /**
   * Get a counterparty by ID
   */
  async getById(id: string): Promise<Counterparty> {
    const response = await api.get<ApiResponse<Counterparty>>(`/counterparties/${id}`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to fetch counterparty');
    }
    return response.data.data;
  },

  /**
   * Create a new counterparty
   */
  async create(data: CreateCounterpartyRequest): Promise<Counterparty> {
    const response = await api.post<ApiResponse<Counterparty>>('/counterparties', data);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to create counterparty');
    }
    return response.data.data;
  },

  /**
   * Update a counterparty
   */
  async update(id: string, data: UpdateCounterpartyRequest): Promise<Counterparty> {
    const response = await api.put<ApiResponse<Counterparty>>(`/counterparties/${id}`, data);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to update counterparty');
    }
    return response.data.data;
  },

  /**
   * Delete a counterparty
   */
  async delete(id: string): Promise<void> {
    const response = await api.delete<ApiResponse<null>>(`/counterparties/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.error?.message || 'Failed to delete counterparty');
    }
  },

  /**
   * Purge all unused counterparties (those with no associated operations or schedulers)
   * @returns Number of counterparties deleted
   */
  async purgeUnused(): Promise<number> {
    const response = await api.delete<ApiResponse<{ deletedCount: number }>>('/counterparties/purge-unused');
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to purge unused counterparties');
    }
    return response.data.data.deletedCount;
  },

  /**
   * Get all counterparty suggestions from multiple sources:
   * - Formal counterparties (with metadata)
   * - Operation counterparty names
   * - Scheduler counterparty names
   *
   * Results are deduplicated and sorted alphabetically.
   *
   * @param searchTerm - Optional search term for filtering
   * @param limit - Maximum number of results (default: 20)
   */
  async getSuggestions(searchTerm?: string, limit: number = 20): Promise<CounterpartyAutocompleteResult[]> {
    const params = new URLSearchParams();
    if (searchTerm) {
      params.append('q', searchTerm);
    }
    params.append('limit', String(limit));

    const response = await api.get<ApiResponse<CounterpartyAutocompleteResult[]>>(
      `/counterparties/suggestions?${params.toString()}`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to fetch counterparty suggestions');
    }
    return response.data.data;
  },

  /**
   * Search counterparties by name (client-side filtering)
   * Returns counterparties whose name contains the search term
   * @deprecated Use getSuggestions instead for more complete results
   */
  async search(searchTerm: string): Promise<Counterparty[]> {
    const counterparties = await this.list();
    if (!searchTerm.trim()) {
      return counterparties;
    }
    const normalizedSearch = searchTerm.toLowerCase().trim();
    return counterparties.filter(c => 
      c.name.toLowerCase().includes(normalizedSearch)
    );
  },

  /**
   * Get the count of operations for a counterparty
   * @param id - Counterparty ID
   * @param accountId - Optional account ID to filter operations
   * @returns Number of operations
   */
  async getOperationsCount(id: string, accountId?: string): Promise<number> {
    const params = new URLSearchParams();
    if (accountId) {
      params.append('accountId', accountId);
    }
    const queryString = params.toString();
    const url = `/counterparties/${id}/operations-count${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get<ApiResponse<{ count: number }>>(url);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to fetch operations count');
    }
    return response.data.data.count;
  },

  /**
   * Get the unique category for a counterparty if all operations have the same category
   * @param id - Counterparty ID
   * @returns Category info or null if multiple categories
   */
  async getUniqueCategory(id: string): Promise<{ id: string; name: string; logoUrl: string | null } | null> {
    const response = await api.get<ApiResponse<{ id: string; name: string; logoUrl: string | null } | null>>(
      `/counterparties/${id}/unique-category`
    );
    if (!response.data.success) {
      throw new Error(response.data.error?.message || 'Failed to fetch unique category');
    }
    return response.data.data || null;
  },

  /**
   * Get the list of accounts where this counterparty is used
   * @param id - Counterparty ID
   * @returns Array of accounts with id, name, and source
   */
  async getAccounts(id: string): Promise<{ id: string; name: string; source?: string }[]> {
    const response = await api.get<ApiResponse<{ id: string; name: string; source?: string }[]>>(
      `/counterparties/${id}/accounts`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to fetch counterparty accounts');
    }
    return response.data.data;
  },

  /**
   * Get the operation types (debit, credit, or both) for a counterparty
   * @param id - Counterparty ID
   * @param accountId - Optional account ID to filter operations
   * @returns Object with hasDebits and hasCredits booleans
   */
  async getOperationTypes(id: string, accountId?: string): Promise<{ hasDebits: boolean; hasCredits: boolean }> {
    const params = new URLSearchParams();
    if (accountId) {
      params.append('accountId', accountId);
    }
    const queryString = params.toString();
    const url = `/counterparties/${id}/operation-types${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get<ApiResponse<{ hasDebits: boolean; hasCredits: boolean }>>(url);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to fetch operation types');
    }
    return response.data.data;
  },

  /**
   * Update the category of all operations for a counterparty
   * @param counterpartyId - Counterparty ID
   * @param categoryId - New category ID to apply
   * @param accountId - Optional account ID to filter operations
   * @param operationType - Optional filter: 'debit' or 'credit'
   * @returns Number of operations updated
   */
  async updateBulkCategory(
    counterpartyId: string,
    categoryId: string,
    accountId?: string,
    operationType?: 'debit' | 'credit'
  ): Promise<number> {
    const response = await api.put<ApiResponse<{ updatedCount: number }>>(
      `/counterparties/${counterpartyId}/bulk-category`,
      { categoryId, accountId, operationType }
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to update operations category');
    }
    return response.data.data.updatedCount;
  },

  /**
   * Merge two counterparties
   * All operations and schedulers from sourceCounterpartyId will be moved to targetCounterpartyId.
   * The source counterparty will be deleted after the merge (unless accountId or operationType is provided for partial merge).
   *
   * @param targetCounterpartyId - ID of the counterparty to keep (merge INTO)
   * @param sourceCounterpartyId - ID of the counterparty to merge FROM (will be deleted)
   * @param accountId - Optional account ID to filter operations and schedulers
   * @param operationType - Optional filter: 'debit' or 'credit'
   * @returns Merge result with counts and the remaining counterparty
   */
  async merge(
    targetCounterpartyId: string,
    sourceCounterpartyId: string,
    accountId?: string,
    operationType?: 'debit' | 'credit'
  ): Promise<MergeCounterpartyResponse> {
    const response = await api.post<ApiResponse<MergeCounterpartyResponse>>(
      `/counterparties/${targetCounterpartyId}/merge`,
      { sourceCounterpartyId, accountId, operationType }
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to merge counterparties');
    }
    return response.data.data;
  },

  /**
   * Rename a counterparty for a specific account only.
   * Creates a new counterparty with the new name and transfers operations/schedulers
   * from the specified account to the new counterparty.
   * The original counterparty remains for other accounts.
   *
   * @param counterpartyId - Original counterparty ID
   * @param name - New name for the counterparty
   * @param logoUrl - Optional new logo URL
   * @param accountId - Account ID to rename for
   * @param operationType - Optional filter: 'debit' or 'credit'
   * @returns Rename result with counts and the new counterparty
   */
  async renameForAccount(
    counterpartyId: string,
    name: string,
    logoUrl: string | undefined,
    accountId: string,
    operationType?: 'debit' | 'credit'
  ): Promise<{ counterparty: Counterparty; operationsUpdated: number; schedulersUpdated: number }> {
    const response = await api.post<ApiResponse<{ counterparty: Counterparty; operationsUpdated: number; schedulersUpdated: number }>>(
      `/counterparties/${counterpartyId}/rename-for-account`,
      { name, logoUrl, accountId, operationType }
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to rename counterparty for account');
    }
    return response.data.data;
  },
};
